// Dashboard - Main competition view
import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, Leaderboard, Button, Avatar } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getOverallLeaderboard, getCategoryLeaderboard } from '../utils/leaderboard';
import { formatDate, formatDateWithDay, isToday, isFuture, isPast, groupGamesByDateSorted, getTodayDateString } from '../utils/dateHelpers';
import { setLastVisitedCompetition } from '../utils/localStorage';
import { signOut } from '../services/authService';
import { uploadCelebrationPhoto, deleteCelebrationPhoto } from '../services/storageService';
import { updateCelebrationPhoto, removeCelebrationPhoto } from '../services/competitionService';
import { checkAllPlayersVoted, getPlayerVotes } from '../services/gameVotingService';

const Dashboard = () => {
  const navigate = useNavigate();
  const { competitionId } = useParams();
  const { user } = useAuth();
  const {
    competition,
    currentPlayer,
    players,
    categories,
    games,
    results,
    isOrganizer,
    refreshCompetition
  } = useCompetition();

  // Celebration photo state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Voting state
  const [votingStatus, setVotingStatus] = useState({ votedCount: 0, totalPlayers: 0, allVoted: false });
  const [playerHasVoted, setPlayerHasVoted] = useState(false);

  // Track which days are expanded (only today by default)
  const [expandedDays, setExpandedDays] = useState(() => {
    const today = getTodayDateString();
    return new Set([today]);
  });

  // Save last visited competition
  useEffect(() => {
    if (competitionId) {
      setLastVisitedCompetition(competitionId);
    }
  }, [competitionId]);

  // Load voting status
  const playerId = user?.odplayerId || user?.oduserId;
  useEffect(() => {
    const loadVotingStatus = async () => {
      if (!competitionId || !players || players.length === 0 || !playerId) return;

      try {
        const playerIds = players.map(p => p.odplayerId);
        const status = await checkAllPlayersVoted(competitionId, playerIds);
        setVotingStatus(status);

        // Check if current player has voted
        const playerVotes = await getPlayerVotes(competitionId, playerId);
        setPlayerHasVoted(playerVotes?.submitted || false);
      } catch (err) {
        console.error('Error loading voting status:', err);
      }
    };

    loadVotingStatus();
  }, [competitionId, players, playerId]);

  // Auto-scroll to playing games
  useEffect(() => {
    if (games && games.length > 0) {
      const playingGames = games.filter(g => g.status === 'playing');
      if (playingGames.length > 0) {
        // Find the first playing game
        const firstPlayingGame = playingGames[0];
        // Expand its day
        if (firstPlayingGame.date) {
          setExpandedDays(prev => new Set([...prev, firstPlayingGame.date]));
        }
        // Scroll to it after a short delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(`game-${firstPlayingGame.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [games]);

  // Calculate overall leaderboard
  const overallLeaderboard = useMemo(() => {
    return getOverallLeaderboard(players, games, results, categories, competition?.rankingMethod || 'byCategory');
  }, [players, games, results, categories, competition]);

  // Get games grouped by date (all games, including past), sorted by time then category
  const gamesByDate = useMemo(() => {
    const allGames = games.filter(g => g.status !== 'voided');
    return groupGamesByDateSorted(allGames, categories);
  }, [games, categories]);

  // Calculate overall completion percentage
  const completionStats = useMemo(() => {
    const totalGames = games.filter(g => g.status !== 'voided').length;
    const playedGames = games.filter(g => g.status === 'played').length;
    const percentage = totalGames > 0 ? Math.round((playedGames / totalGames) * 100) : 0;
    return { totalGames, playedGames, percentage };
  }, [games]);

  // Recent activity - only the last played game with results
  const lastPlayedGame = useMemo(() => {
    const playedGames = games
      .filter(g => g.status === 'played')
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    if (playedGames.length === 0) return null;

    const game = playedGames[0];
    const gameResults = results
      .filter(r => r.gameId === game.id && r.participated && r.pointsEntered !== null)
      .map(r => ({
        ...r,
        player: players.find(p => p.id === r.playerId)
      }))
      .sort((a, b) => b.pointsEntered - a.pointsEntered);

    return {
      game,
      playerResults: gameResults
    };
  }, [games, results, players]);

  // Active leaderboard view (overall or category)
  const [activeLeaderboardView, setActiveLeaderboardView] = React.useState('overall');

  // Category leaders
  const categoryLeaders = useMemo(() => {
    return categories
      .filter(cat => cat.status === 'active')
      .map(category => {
        const leaderboard = getCategoryLeaderboard(players, games, results, category.id);
        const leader = leaderboard[0];
        const categoryGames = games.filter(g => g.categoryId === category.id);
        const playedCategoryGames = categoryGames.filter(g => g.status === 'played');
        const progress = categoryGames.length > 0
          ? Math.round((playedCategoryGames.length / categoryGames.length) * 100)
          : 0;
        const leaderPlayer = leader ? players.find(p => p.id === leader.playerId) : null;

        return {
          category,
          leader: leader || null,
          leaderPlayer,
          progress,
          totalGames: categoryGames.length,
          playedGames: playedCategoryGames.length
        };
      });
  }, [categories, players, games, results]);

  const handleGameClick = (gameId) => {
    navigate(`/competition/${competition.id}/game/${gameId}`);
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/competition/${competition.id}/category/${categoryId}`);
  };

  const handleAdminClick = () => {
    navigate(`/competition/${competition.id}/admin`);
  };

  const handleProfileClick = () => {
    navigate(`/competition/${competition.id}/profile`);
  };

  const handlePlayerClick = (playerId) => {
    navigate(`/competition/${competition.id}/player/${playerId}`);
  };

  const handleSignOut = () => {
    // Full page navigation to home with signout flag
    // This leaves the competition page immediately before any auth state changes
    window.location.href = '/?signout=true';
  };

  const handleHallOfFameClick = () => {
    navigate(`/competition/${competition.id}/hall-of-fame`);
  };

  const toggleDayExpanded = (date) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const getGameStatusBadge = (game) => {
    if (game.status === 'played') {
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Played</span>;
    }
    if (game.status === 'playing') {
      return <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded font-semibold animate-pulse">Playing</span>;
    }
    return <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Planned</span>;
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoError('');
    setPhotoSuccess('');
    setUploadingPhoto(true);

    try {
      // Upload photo to storage
      const photoUrl = await uploadCelebrationPhoto(competitionId, file);

      // Update competition with photo URL
      await updateCelebrationPhoto(competitionId, photoUrl);

      // Refresh competition data
      await refreshCompetition();

      setPhotoSuccess('Celebration photo uploaded successfully!');
      setTimeout(() => setPhotoSuccess(''), 3000);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setPhotoError(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm('Are you sure you want to remove the celebration photo?')) {
      return;
    }

    setPhotoError('');
    setPhotoSuccess('');
    setDeletingPhoto(true);

    try {
      // Delete photo from storage
      if (competition.celebrationPhotoUrl) {
        await deleteCelebrationPhoto(competition.celebrationPhotoUrl);
      }

      // Remove photo URL from competition
      await removeCelebrationPhoto(competitionId);

      // Refresh competition data
      await refreshCompetition();

      setPhotoSuccess('Celebration photo removed successfully!');
      setTimeout(() => setPhotoSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting photo:', error);
      setPhotoError(error.message || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header - Optimized for Mobile */}
      <div className="bg-white shadow sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dashboard</p>
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{competition.name}</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {formatDate(competition.startDate)} - {formatDate(competition.endDate)}
              </p>
              {currentPlayer && (
                <p className="text-xs sm:text-sm text-gray-700 mt-1">
                  <span className="font-semibold">{currentPlayer.displayName}</span>
                  {isOrganizer && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Organizer</span>}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Celebration Photo Banner - Only for finished competitions */}
      {competition.status === 'finished' && (
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-b-4 border-yellow-400">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
            {competition.celebrationPhotoUrl ? (
              <div className="relative">
                <img
                  src={competition.celebrationPhotoUrl}
                  alt="Competition Celebration"
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                  style={{ aspectRatio: '16/9' }}
                />
                {isOrganizer && (
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button
                      onClick={handlePhotoDelete}
                      disabled={deletingPhoto}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
                    >
                      {deletingPhoto ? 'Removing...' : 'Remove Photo'}
                    </Button>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                  <h2 className="text-white text-xl sm:text-2xl font-bold">🏆 Champions Celebration!</h2>
                  <p className="text-white/90 text-sm mt-1">Competition completed - {formatDate(competition.endDate)}</p>
                </div>
              </div>
            ) : (
              isOrganizer && (
                <div className="text-center py-8 border-2 border-dashed border-yellow-300 rounded-lg bg-white/50">
                  <div className="mb-4">
                    <span className="text-6xl">📸</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Add a Celebration Photo</h3>
                  <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                    Upload a group photo to commemorate the competition winner and celebrate everyone's participation!
                  </p>
                  <label className="inline-block">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="hidden"
                    />
                    <Button
                      as="span"
                      disabled={uploadingPhoto}
                      className="cursor-pointer"
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">16:9 aspect ratio recommended • Max 5MB</p>
                </div>
              )
            )}
            {photoError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {photoError}
              </div>
            )}
            {photoSuccess && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {photoSuccess}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Voting Section - Shows when no games scheduled and schedule not confirmed */}
      {competition?.leagueId && !competition?.scheduleConfirmed && games.length === 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-b-4 border-purple-400">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
            <Card className="bg-white/80 backdrop-blur">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span>🗳️</span> Vote for Games
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Help decide which games to play! Vote for your top 3 games in each category.
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className={`px-2 py-1 rounded ${
                      playerHasVoted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {playerHasVoted ? 'You voted ✓' : 'You have not voted yet'}
                    </span>
                    <span className="text-gray-500">
                      {votingStatus.votedCount}/{votingStatus.totalPlayers} players voted
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/competition/${competitionId}/voting`)}
                  className="whitespace-nowrap"
                >
                  {playerHasVoted ? 'View Voting' : 'Vote Now'}
                </Button>
              </div>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all duration-300"
                    style={{ width: `${votingStatus.totalPlayers > 0 ? (votingStatus.votedCount / votingStatus.totalPlayers) * 100 : 0}%` }}
                  />
                </div>
                {votingStatus.allVoted && votingStatus.totalPlayers > 0 && (
                  <p className="text-sm text-purple-700 font-medium mt-2">
                    All players have voted! {isOrganizer ? 'You can now view results and confirm the schedule.' : 'Waiting for organizer to confirm schedule.'}
                  </p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
          {/* Main Leaderboard - Takes 2 columns */}
          <div className="lg:col-span-2">
            <Card className="mb-8">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Leaderboard</h2>
                {/* Category Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  <button
                    onClick={() => setActiveLeaderboardView('overall')}
                    className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                      activeLeaderboardView === 'overall'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🏆 Overall
                  </button>
                  {categories.filter(c => c.status === 'active').map(category => (
                    <button
                      key={category.id}
                      onClick={() => setActiveLeaderboardView(category.id)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                        activeLeaderboardView === category.id
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={{
                        backgroundColor: activeLeaderboardView === category.id ? category.color : undefined
                      }}
                    >
                      {category.icon} {category.name}
                    </button>
                  ))}
                </div>
              </div>
              {activeLeaderboardView === 'overall' ? (
                overallLeaderboard.length > 0 ? (
                  <>
                    <Leaderboard
                      scores={overallLeaderboard.slice(0, 6)}
                      players={players}
                      onPlayerClick={handlePlayerClick}
                      showCategoryBreakdown={true}
                      categories={categories.filter(c => c.status === 'active')}
                    />
              {overallLeaderboard.length > 6 && (
                <div className="mt-4">
                  <details className="cursor-pointer">
                    <summary className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                      Show all {overallLeaderboard.length} players
                    </summary>
                    <div className="mt-4">
                      <Leaderboard
                        scores={overallLeaderboard.slice(6)}
                        players={players}
                        onPlayerClick={handlePlayerClick}
                        showCategoryBreakdown={true}
                        categories={categories.filter(c => c.status === 'active')}
                      />
                    </div>
                  </details>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-4">No games played yet</p>
          )
        ) : (
          // Category leaderboard view
          (() => {
            const category = categories.find(c => c.id === activeLeaderboardView);
            const categoryLeaderboard = getCategoryLeaderboard(players, games, results, activeLeaderboardView);
            return categoryLeaderboard.length > 0 ? (
              <>
                <Leaderboard
                  scores={categoryLeaderboard.slice(0, 6)}
                  players={players}
                  onPlayerClick={handlePlayerClick}
                />
                {categoryLeaderboard.length > 6 && (
                  <div className="mt-4">
                    <details className="cursor-pointer">
                      <summary className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                        Show all {categoryLeaderboard.length} players
                      </summary>
                      <div className="mt-4">
                        <Leaderboard
                          scores={categoryLeaderboard.slice(6)}
                          players={players}
                          onPlayerClick={handlePlayerClick}
                        />
                      </div>
                    </details>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-center py-4">No games played in this category yet</p>
            );
          })()
        )}
            </Card>
          </div>

          {/* Recent Activity Feed - Takes 1 column */}
          <div className="lg:col-span-1">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Last Result</h2>
                {lastPlayedGame && (
                  <button
                    onClick={() => navigate(`/competition/${competitionId}/game-log`)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View All →
                  </button>
                )}
              </div>
              {lastPlayedGame ? (
                <div>
                  {(() => {
                    const { game, playerResults } = lastPlayedGame;
                    const category = categories.find(c => c.id === game.categoryId);
                    const isNew = isPast(game.date) && new Date() - new Date(game.date) < 24 * 60 * 60 * 1000;
                    return (
                      <div
                        onClick={() => handleGameClick(game.id)}
                        className="p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {category && <span className="text-lg">{category.icon}</span>}
                            <div>
                              <p className="font-semibold text-sm text-gray-900">{game.name}</p>
                              <p className="text-xs text-gray-500">{formatDate(game.date)}</p>
                            </div>
                          </div>
                          {isNew && (
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                              NEW
                            </span>
                          )}
                        </div>
                        {playerResults.length > 0 ? (
                          <div className="ml-7 space-y-1.5">
                            {playerResults.slice(0, 6).map((result, idx) => (
                              <div key={result.playerId} className="flex items-center gap-2">
                                <div className="flex items-center gap-2 flex-1">
                                  <Avatar player={result.player} size="sm" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{result.player?.displayName}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {idx === 0 && <span className="text-xs">🥇</span>}
                                  {idx === 1 && <span className="text-xs">🥈</span>}
                                  {idx === 2 && <span className="text-xs">🥉</span>}
                                  <p className="text-sm font-bold" style={{ color: category?.color }}>
                                    {result.pointsEntered}
                                  </p>
                                </div>
                              </div>
                            ))}
                            {playerResults.length > 6 && (
                              <p className="text-xs text-gray-500 mt-1">
                                +{playerResults.length - 6} more player{playerResults.length - 6 > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 ml-7">No results entered</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No games played yet</p>
                  <p className="text-gray-400 text-xs mt-1">Results will appear here</p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Category Cards */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {categoryLeaders.map(({ category, leader, leaderPlayer, progress, totalGames, playedGames }) => (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className="cursor-pointer p-4 rounded-lg border-2 border-transparent hover:border-blue-500 transition-colors"
                style={{ backgroundColor: `${category.color || '#3B82F6'}15` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{category.icon || '🎲'}</span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {category.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-700">{progress}%</div>
                    <div className="text-xs text-gray-500">
                      {playedGames}/{totalGames}
                    </div>
                  </div>
                </div>
                {leader && leaderPlayer ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar player={leaderPlayer} size="sm" />
                      <div>
                        <p className="text-xs text-gray-600">Current Leader</p>
                        <p className="text-base font-bold text-gray-900">
                          {leader.playerName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{leader.score.toFixed(1)}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No games played</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Schedule - Games by Date */}
        {gamesByDate.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Schedule</h2>
              <div className="text-left sm:text-right">
                <div className="text-sm text-gray-600">Overall Progress</div>
                <div className="flex items-center gap-2">
                  <div className="text-lg font-bold text-blue-600">{completionStats.percentage}%</div>
                  <div className="text-sm text-gray-500">
                    ({completionStats.playedGames}/{completionStats.totalGames} games)
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {gamesByDate.map(({ date, games: dateGames }) => {
                const todayDate = isToday(date);
                const dayPlayedGames = dateGames.filter(g => g.status === 'played').length;
                const dayPercentage = dateGames.length > 0 ? Math.round((dayPlayedGames / dateGames.length) * 100) : 0;
                const isExpanded = expandedDays.has(date);

                return (
                  <Card key={date} className={todayDate ? 'border-2 border-blue-500' : ''}>
                    <div
                      className="flex items-center justify-between cursor-pointer select-none"
                      onClick={() => toggleDayExpanded(date)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 transition-transform" style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                          ▼
                        </span>
                        <h3 className={`text-lg font-bold ${todayDate ? 'text-blue-600' : 'text-gray-900'}`}>
                          {formatDateWithDay(date)}
                        </h3>
                        {todayDate && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                            TODAY
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          ({dateGames.length} {dateGames.length === 1 ? 'game' : 'games'})
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-700">{dayPercentage}% complete</div>
                        <div className="text-xs text-gray-500">
                          {dayPlayedGames}/{dateGames.length} played
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="space-y-2 mt-3">
                        {dateGames.map((game, index) => {
                          const category = categories.find(c => c.id === game.categoryId);

                          // Helper function to format time to HH:MM
                          const formatTimeHHMM = (timeString) => {
                            if (!timeString) return '';
                            // Handle both "HH:MM" and full timestamp formats
                            if (timeString.includes('T')) {
                              const date = new Date(timeString);
                              return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                            }
                            return timeString.substring(0, 5); // Get HH:MM from HH:MM:SS or keep HH:MM
                          };

                          // Calculate duration in readable format
                          const calculateDuration = (start, end) => {
                            const startTime = formatTimeHHMM(start);
                            const endTime = formatTimeHHMM(end);
                            const [startHour, startMin] = startTime.split(':').map(Number);
                            const [endHour, endMin] = endTime.split(':').map(Number);
                            const startMinutes = startHour * 60 + startMin;
                            const endMinutes = endHour * 60 + endMin;
                            const durationMinutes = endMinutes - startMinutes;
                            const hours = Math.floor(durationMinutes / 60);
                            const minutes = durationMinutes % 60;

                            if (hours > 0 && minutes > 0) {
                              return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min${minutes > 1 ? 's' : ''}`;
                            } else if (hours > 0) {
                              return `${hours} hour${hours > 1 ? 's' : ''}`;
                            } else {
                              return `${minutes} min${minutes > 1 ? 's' : ''}`;
                            }
                          };

                          // Check if we need to show a break before this game
                          const showBreak = index > 0 && (game.startTime || game.actualStartTime);
                          let breakDuration = null;

                          if (showBreak) {
                            const prevGame = dateGames[index - 1];
                            const prevEndTime = prevGame.actualEndTime || prevGame.startTime;
                            const currentStartTime = game.actualStartTime || game.startTime;

                            if (prevEndTime && currentStartTime) {
                              const prevEnd = formatTimeHHMM(prevEndTime);
                              const currentStart = formatTimeHHMM(currentStartTime);
                              const [prevEndHour, prevEndMin] = prevEnd.split(':').map(Number);
                              const [currentStartHour, currentStartMin] = currentStart.split(':').map(Number);
                              const prevEndMinutes = prevEndHour * 60 + prevEndMin;
                              const currentStartMinutes = currentStartHour * 60 + currentStartMin;
                              const gapMinutes = currentStartMinutes - prevEndMinutes;

                              if (gapMinutes > 0) {
                                const hours = Math.floor(gapMinutes / 60);
                                const minutes = gapMinutes % 60;

                                if (hours > 0 && minutes > 0) {
                                  breakDuration = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min${minutes > 1 ? 's' : ''}`;
                                } else if (hours > 0) {
                                  breakDuration = `${hours} hour${hours > 1 ? 's' : ''}`;
                                } else {
                                  breakDuration = `${minutes} min${minutes > 1 ? 's' : ''}`;
                                }
                              }
                            }
                          }

                          return (
                            <React.Fragment key={game.id}>
                              {breakDuration && (
                                <div className="flex items-center justify-center p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="text-xs text-gray-600 font-medium">
                                    ☕ Break: {breakDuration}
                                  </p>
                                </div>
                              )}
                              <div
                                id={`game-${game.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGameClick(game.id);
                                }}
                                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                                style={{ backgroundColor: category ? `${category.color}10` : '#f9fafb' }}
                              >
                                <div className="flex items-center gap-3">
                                  {category && <span className="text-2xl">{category.icon}</span>}
                                  <div>
                                    <p className="font-semibold text-gray-900">{game.name}</p>
                                    <p className="text-sm text-gray-600">{category?.name}</p>
                                    {game.status === 'played' && game.actualStartTime && game.actualEndTime ? (
                                      <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                        {game.startTime && (
                                          <p>🕐 Scheduled: {formatTimeHHMM(game.startTime)}</p>
                                        )}
                                        <p>⏱️ Start: {formatTimeHHMM(game.actualStartTime)} End: {formatTimeHHMM(game.actualEndTime)}</p>
                                        <p>⏳ Lasted: {calculateDuration(game.actualStartTime, game.actualEndTime)}</p>
                                      </div>
                                    ) : game.startTime ? (
                                      <p className="text-xs text-gray-500 mt-1">🕐 {formatTimeHHMM(game.startTime)}</p>
                                    ) : null}
                                  </div>
                                </div>
                                {getGameStatusBadge(game)}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Competition Info */}
        <Card title="Competition Info">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-semibold capitalize">{competition.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Players:</span>
              <span className="font-semibold">{players.length} / {competition.maxPlayers}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Categories:</span>
              <span className="font-semibold">{categories.filter(c => c.status === 'active').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Games Played:</span>
              <span className="font-semibold">{games.filter(g => g.status === 'played').length}</span>
            </div>
            {isOrganizer && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Invite Code:</span>
                  <span className="font-mono text-lg font-bold text-blue-600">
                    {competition.inviteCode}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Version Info */}
        <div className="text-center py-4 text-xs text-gray-400">
          v{require('../../package.json').version}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
